import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HttpService from '../../service/HttpService';
import PrintReportButton from './PrintReportButton';
import ReactSelect from 'react-select'; // Add this import at the top


const STATUS_OPTIONS = [
    { value: 'NO', label: 'Not OK' },
    { value: 'OVER', label: 'Over' },
    { value: 'NA', label: 'Not Applicable' },
    { value: 'OK', label: 'OK' }
];


const ScanningSessionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = HttpService();
    const [session, setSession] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ show: false, action: null });
    const [actionLoading, setActionLoading] = useState(false);
    const [errorModal, setErrorModal] = useState({ show: false, message: '' });
    const [search, setSearch] = useState({
        partNumber: '',
        description: '',
        deliveryNotes: '',
        status: [] // now an array for multi-select
    });

    useEffect(() => {
        const fetchSessionDetail = async () => {
            setIsLoading(true);
            try {
                const response = await axiosInstance.get(`/scanning-sessions/snapshots/${id}`);
                setSession(response.data);
            } catch (error) {
                console.error('Failed to fetch session details:', error);
                setError('Failed to load session details. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSessionDetail();
    }, [id]);

    const goBack = () => {
        navigate('/dashboard/scanning-session');
    };

    const handleStatusClick = () => {
        // Determine which action to take based on current status
        const action = session.closed ? 'reopen' : 'close';
        // Show confirmation modal with appropriate action
        setConfirmModal({ show: true, action });
    };

    const handleStatusChange = async () => {
        setActionLoading(true);
        try {
            // Determine which API endpoint to call based on current status
            const endpoint = session.closed
                ? `/scanning-sessions/${id}/reopen`
                : `/scanning-sessions/${id}/close`;

            // Call the API
            await axiosInstance.put(endpoint);

            // Update the local session state
            setSession({
                ...session,
                closed: !session.closed
            });

            // Hide the confirmation modal
            setConfirmModal({ show: false, action: null });
        } catch (error) {
            console.error(`Failed to ${confirmModal.action} session:`, error);
            // Show error modal
            const errorMessage = error.response?.data?.message ||
                `Failed to ${confirmModal.action} session. Please try again.`;
            setErrorModal({ show: true, message: errorMessage });
        } finally {
            setActionLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="dashboard-section">
                <div className="bg-white rounded-xl shadow-sm p-6 text-center">
                    <div className="animate-pulse">Loading session details...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-section">
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="text-center text-red-600">{error}</div>
                    <div className="mt-4 text-center">
                        <button
                            onClick={goBack}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!session) {
        return (
            <div className="dashboard-section">
                <div className="bg-white rounded-xl shadow-sm p-6 text-center">
                    <div>Session not found</div>
                    <div className="mt-4">
                        <button
                            onClick={goBack}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Helper to filter items
    const filteredItems = session.items?.filter(item =>
        (search.partNumber === '' || item.partNumber?.toLowerCase().includes(search.partNumber.toLowerCase())) &&
        (search.description === '' || item.description?.toLowerCase().includes(search.description.toLowerCase())) &&
        (search.deliveryNotes === '' || (item.deliveryNotes?.join(', ') || '').toLowerCase().includes(search.deliveryNotes.toLowerCase())) &&
        (search.status.length === 0 || search.status.includes(item.status))
    );

    return (
        <div className="dashboard-section">
            {/* Header Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <div className="flex items-center mb-6">
                    <button
                        onClick={goBack}
                        className="mr-4 text-gray-600 hover:text-gray-900"
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h2 className="text-xl font-semibold text-gray-800">{session.title}</h2>
                </div>

                {/* Second line: Delivery Notes | Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <p className="text-sm text-gray-600">Delivery Notes</p>
                        <p className="font-medium">
                            {session.deliveryNotes && session.deliveryNotes.length > 0
                                ? session.deliveryNotes.join(', ')
                                : 'None'}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Status</p>
                        {/* Make status indicator clickable */}
                        <button
                            onClick={handleStatusClick}
                            className={`inline-block px-4 py-2 rounded-lg text-sm font-semibold mt-1 
                                ${session.closed
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'} 
                                transition-colors`}
                            title={`Click to ${session.closed ? 'reopen' : 'close'} this session`}
                        >
                            {session.closed ? 'Closed' : 'Open'}
                        </button>
                    </div>
                </div>

                {/* Third line: Submitted Date | Submitted By */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-gray-600">Submitted Date</p>
                        <p className="font-medium">
                            {session.createdDate
                                ? new Intl.DateTimeFormat('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                }).format(new Date(session.createdDate))
                                : '-'}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600">Submitted By</p>
                        <p className="font-medium">{session.createdBy}</p>
                    </div>
                    <div>
                        <PrintReportButton sessionId={session.id} sessionTitle={session.title} />
                    </div>
                </div>
            </div>

            {/* Items Section - Placeholder for future implementation */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Items</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                        <thead>
                            <tr>
                                <th className="px-3 py-2 border-b align-top">
                                    <div>Part Number</div>
                                    <input
                                        className="mt-1 w-full px-2 py-1 border rounded text-xs"
                                        placeholder="Search..."
                                        value={search.partNumber}
                                        onChange={e => setSearch({ ...search, partNumber: e.target.value })}
                                    />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div>Description</div>
                                    <input
                                        className="mt-1 w-full px-2 py-1 border rounded text-xs"
                                        placeholder="Search..."
                                        value={search.description}
                                        onChange={e => setSearch({ ...search, description: e.target.value })}
                                    />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div>Ship Qtd</div>
                                    <div style={{ height: '30px' }} /> {/* Empty div for alignment */}
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div>Quantity</div>
                                    <div style={{ height: '30px' }} /> {/* Empty div for alignment */}
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div>Input Type</div>
                                    <div style={{ height: '30px' }} /> {/* Empty div for alignment */}
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div>Delivery Notes</div>
                                    <input
                                        className="mt-1 w-full px-2 py-1 border rounded text-xs"
                                        placeholder="Search..."
                                        value={search.deliveryNotes}
                                        onChange={e => setSearch({ ...search, deliveryNotes: e.target.value })}
                                    />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div>Status</div>
                                    <ReactSelect
                                        isMulti
                                        options={STATUS_OPTIONS}
                                        value={STATUS_OPTIONS.filter(opt => search.status.includes(opt.value))}
                                        onChange={selected => setSearch({ ...search, status: selected.map(opt => opt.value) })}
                                        placeholder="Select status..."
                                        className="mt-1 text-xs"
                                        styles={{
                                            control: base => ({ ...base, minHeight: '38px', fontSize: '0.75rem' }),
                                            multiValue: base => ({ ...base, backgroundColor: '#e5e7eb', color: '#374151' }),
                                            multiValueLabel: base => ({ ...base, color: '#374151' }),
                                            multiValueRemove: base => ({ ...base, color: '#374151' }),
                                        }}
                                    />
                                </th>
                                <th className="px-3 py-2 border-b align-top">
                                    <div>Time</div>
                                    <div style={{ height: '30px' }} /> {/* Empty div for alignment */}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems && filteredItems.length > 0 ? (
                                filteredItems.map((item, idx) => (
                                    <tr key={idx} className="border-b hover:bg-gray-50">
                                        <td className="px-3 py-2">{item.partNumber}</td>
                                        <td className="px-3 py-2">{item.description}</td>
                                        <td className="px-3 py-2">{item.shipQtd}</td>
                                        <td className="px-3 py-2">{item.quantity}</td>
                                        <td className="px-3 py-2">{item.inputType}</td>
                                        <td className="px-3 py-2">{item.deliveryNotes?.join(', ')}</td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-block px-2 py-1 rounded text-xs font-semibold
                                                ${item.status === 'OK'
                                                    ? 'bg-green-100 text-green-700'
                                                    : item.status === 'NO'
                                                        ? 'bg-red-100 text-red-700'
                                                        : item.status === 'OVER'
                                                            ? 'bg-yellow-100 text-yellow-700'
                                                            : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                {STATUS_OPTIONS.find(opt => opt.value === item.status)?.label || item.status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">{item.time}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="text-center py-4 text-gray-400">
                                        No items found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Confirmation Modal */}
            {confirmModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            {confirmModal.action === 'close' ? 'Close Session' : 'Reopen Session'}
                        </h3>
                        <p className="text-gray-700 mb-6">
                            {confirmModal.action === 'close'
                                ? 'Are you sure you want to close this session? This action will mark the session as completed.'
                                : 'Are you sure you want to reopen this session? This will allow further modifications.'}
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                                onClick={() => setConfirmModal({ show: false, action: null })}
                                disabled={actionLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className={`px-4 py-2 rounded-lg text-white transition ${confirmModal.action === 'close'
                                    ? 'bg-yellow-500 hover:bg-yellow-600'
                                    : 'bg-green-500 hover:bg-green-600'
                                    }`}
                                onClick={handleStatusChange}
                                disabled={actionLoading}
                            >
                                {actionLoading ? 'Processing...' : confirmModal.action === 'close' ? 'Close' : 'Reopen'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {errorModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md text-center">
                        <h3 className="text-lg font-semibold text-red-600 mb-4">Error</h3>
                        <p className="text-gray-700 mb-4">{errorModal.message}</p>
                        <button
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                            onClick={() => setErrorModal({ show: false, message: '' })}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScanningSessionDetail;